import { useRef, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input, PageHeader, Pill, Table, Td, Th } from "@/components/ui";
import { popup, confirmPopup } from "@/components/popup";

interface Sub {
  id: number;
  user_id: number;
  username: string | null;
  kind: string;
  plan: string;
  endpoint_id: number | null;
  endpoint_name: string | null;
  endpoint_address: string | null;
  backend: string;
  device_limit: number;
  is_active: boolean;
  activated_at: string;
  expires_at: string | null;
  secret_preview: string | null;
  link: string | null;
}

interface VpnSub {
  telegram_id: number | null;
  username: string;
  status: string | null;
  expires_at: string | null;
  devices: number | null;
  used_traffic: number | null;
  short_uuid: string | null;
  user_id: number | null;
  local_username: string | null;
  // trial | paid из локального учёта; null — юзер выдан до его появления
  plan?: string | null;
}

function fmt(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(+d)
    ? s
    : d.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}
/** Срок как в «Доступах»: крупно сколько осталось, снизу точная дата. */
function renderTerm(expires: string | null) {
  if (!expires) return <span className="text-zinc-600 text-sm">бессрочно</span>;
  const left = Math.ceil((new Date(expires).getTime() - Date.now()) / 86400000);
  const expired = left <= 0;
  return (
    <div className="min-w-0">
      <div
        className={`text-sm tabular-nums ${
          expired ? "text-zinc-500" : left <= 3 ? "text-amber-300" : "text-zinc-200"
        }`}
      >
        {expired ? "истёк" : `${left} дн.`}
      </div>
      <div className="text-[11px] text-zinc-600 tabular-nums">{fmt(expires)}</div>
    </div>
  );
}

function fmtBytes(n: number | null): string {
  if (!n) return "0";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

// free_ad убран: ad-хосты — бесплатные прокси с рекламным тегом, подписок там
// не бывает, и фильтр всегда давал пустую таблицу.
const PLANS = ["", "paid", "trial"];
const PLAN_LABEL: Record<string, string> = {
  "": "Все",
  paid: "Платные",
  trial: "Пробные",
};

export default function Subscriptions() {
  const [sp] = useSearchParams();
  const [tab, setTab] = useState<"proxy" | "vpn">(
    sp.get("tab") === "vpn" ? "vpn" : "proxy",
  );
  const counts = useQuery<{ total_proxy: number; total_vpn: number }>({
    queryKey: ["sub-counts"],
    queryFn: () => api("/api/admin/subscriptions?status="),
    refetchInterval: 30000,
  });
  // VPN теперь учитывается локально (services/vpn_ledger.py) — счётчик берём из
  // того же источника, что и Proxy. Раньше тут был размер списка из Remnawave,
  // где триал и платный неразличимы, и число не сходилось с таблицей.
  const vpnN = counts.data?.total_vpn ?? null;

  return (
    <div className="p-4 md:p-8">
      <PageHeader title="Подписки" />
      <div className="flex items-center gap-2 mb-5">
        <TabBtn active={tab === "proxy"} onClick={() => setTab("proxy")}>
          Proxy <span className="opacity-60">{counts.data?.total_proxy ?? ""}</span>
        </TabBtn>
        <TabBtn active={tab === "vpn"} onClick={() => setTab("vpn")}>
          VPN <span className="opacity-60">{vpnN ?? ""}</span>
        </TabBtn>
      </div>
      {tab === "proxy" ? <ProxyTab /> : <VpnTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`h-8 px-4 rounded-lg text-sm font-medium border transition-all ${
        active ? "bg-brand-soft text-brand border-brand/40 glow-ring" : "bg-black/20 border-white/[0.08] text-zinc-400 hover:text-zinc-200"
      }`}
    >
      {children}
    </button>
  );
}

function ProxyTab() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("active");

  // Срезы приходят с дашборда: «62 заканчиваются завтра» должно вести именно
  // на эти 62, а не на всю таблицу.
  const expiring = sp.get("expiring_days");
  const churned = sp.get("churned_days");

  const params = new URLSearchParams({ kind: "proxy" });
  if (search.trim()) params.set("search", search.trim());
  if (plan) params.set("plan", plan);
  if (expiring) params.set("expiring_days", expiring);
  else if (churned) params.set("churned_days", churned);
  else if (status) params.set("status", status);

  const q = useQuery<{ subscriptions: Sub[]; total: number }>({
    queryKey: ["subscriptions", "proxy", search.trim(), plan, status, expiring, churned],
    queryFn: () => api(`/api/admin/subscriptions?${params.toString()}`),
    refetchInterval: 20000,
  });
  const sliceLabel = expiring
    ? `истекают в ближайшие ${expiring} дн.`
    : churned
      ? `истекли за последние ${churned} дн. и не продлены`
      : null;
  const subs = q.data?.subscriptions ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["subscriptions"] });

  return (
    <>
      {sliceLabel && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2.5 rounded-xl bg-brand-soft border border-brand/30 flex-wrap">
          <span className="text-sm text-brand font-medium">Срез с дашборда: {sliceLabel}</span>
          <span className="text-sm text-zinc-400 tabular-nums">{q.data?.subscriptions.length ?? 0} шт.</span>
          <button
            onClick={() => setSp({}, { replace: true })}
            className="ml-auto text-sm text-zinc-400 hover:text-zinc-100"
          >
            Показать все
          </button>
        </div>
      )}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Input
          placeholder="Поиск: user_id, telegram_id или @username"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-80"
        />
        <div className="flex items-center gap-1">
          {PLANS.map((p) => (
            <FilterChip key={p || "all"} active={plan === p} onClick={() => setPlan(p)}>
              {PLAN_LABEL[p]}
            </FilterChip>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {[["active", "Активные"], ["", "Все"], ["inactive", "Выкл"]].map(([k, l]) => (
            <FilterChip key={k || "all"} active={status === k} onClick={() => setStatus(k)}>
              {l}
            </FilterChip>
          ))}
        </div>
        <div className="ml-auto text-zinc-500 text-sm">
          найдено: {(q.data?.total ?? subs.length).toLocaleString("ru-RU")}
          {q.data && q.data.total > subs.length && (
            <span className="text-zinc-600"> · показано {subs.length}</span>
          )}
        </div>
      </div>

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}
      {!q.isLoading && subs.length === 0 && <div className="text-zinc-500">Ничего не найдено.</div>}

      {subs.length > 0 && (
        <Table>
          {/* Фиксированные ширины: иначе колонки прыгают при смене фильтра —
              «платный»/«за друзей» разной длины, и таблица переразмечается. */}
          <colgroup>
            <col />
            <col style={{ width: "260px" }} />
            <col style={{ width: "130px" }} />
            <col style={{ width: "130px" }} />
            <col style={{ width: "150px" }} />
            <col style={{ width: "60px" }} />
          </colgroup>
          <thead>
            <tr>
              <Th>Пользователь</Th>
              <Th>Хост</Th>
              <Th>Тип</Th>
              <Th>Статус</Th>
              <Th>Срок</Th>
              <Th align="right" fit>{" "}</Th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <ProxyRow key={s.id} s={s} onChanged={refresh} />
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

function ProxyRow({ s, onChanged }: { s: Sub; onChanged: () => void }) {
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menu]);

  const act = (path: string, method = "POST", body?: unknown) =>
    api(`/api/admin/subscriptions/${s.id}${path}`, { method, body: body ? JSON.stringify(body) : undefined });

  const extend = useMutation({
    mutationFn: () => act("/extend", "POST", { days: 30 }),
    onSuccess: () => { popup({ tone: "success", title: "Продлено +30 дней", body: "Юзеру ушло уведомление." }); onChanged(); },
    onError: (e) => popup({ tone: "error", title: "Продление", body: (e as Error).message }),
  });
  const rotate = useMutation({
    mutationFn: () => act("/rotate"),
    onSuccess: () => { popup({ tone: "success", title: "Секрет сменён", body: "Старая ссылка мертва, юзеру ушла новая." }); onChanged(); },
    onError: (e) => popup({ tone: "error", title: "Ротация", body: (e as Error).message }),
  });
  const deactivate = useMutation({
    mutationFn: () => act("/deactivate"),
    onSuccess: () => { popup({ tone: "success", title: "Деактивирована" }); onChanged(); },
    onError: (e) => popup({ tone: "error", title: "Деактивация", body: (e as Error).message }),
  });
  const del = useMutation({
    mutationFn: () => act("", "DELETE"),
    onSuccess: () => { popup({ tone: "success", title: "Удалена" }); onChanged(); },
    onError: (e) => popup({ tone: "error", title: "Удаление", body: (e as Error).message }),
  });

  const busy = extend.isPending || rotate.isPending || deactivate.isPending || del.isPending;

  return (
    <tr
      className={`group hover:bg-white/[0.03] transition-colors ${
        s.is_active ? "" : "opacity-55"
      }`}
    >
      <Td>
        <div className="min-w-0">
          <Link
            to={`/users/${s.user_id}`}
            className="block truncate text-zinc-100 hover:text-brand font-medium"
          >
            {s.username ? `@${s.username}` : `#${s.user_id}`}
          </Link>
          <div className="text-[11px] text-zinc-500 font-mono">
            user #{s.user_id} · подписка #{s.id}
            {s.secret_preview ? ` · ${s.secret_preview}…` : ""}
          </div>
        </div>
      </Td>
      <Td>
        {s.endpoint_id ? (
          <>
            <Link to="/endpoints" className="text-zinc-300 hover:text-brand">
              {s.endpoint_name ?? `ep#${s.endpoint_id}`}
            </Link>
            <div className="text-[11px] text-zinc-600 font-mono">
              {s.endpoint_address ?? `#${s.endpoint_id}`} · {s.device_limit} устр.
            </div>
          </>
        ) : (
          <>
            <span className="text-amber-300/80">хост не задан</span>
            <div className="text-[11px] text-zinc-600">ссылку собрать не из чего</div>
          </>
        )}
      </Td>
      <Td>
        <Pill tone={s.plan === "paid" ? "success" : "neutral"}>
          {s.plan === "paid" ? "платный" : s.plan === "trial" ? "пробный" : s.plan}
        </Pill>
      </Td>
      <Td>
        <Pill tone={s.is_active ? "success" : "neutral"}>
          {s.is_active ? "активна" : "выключена"}
        </Pill>
      </Td>
      <Td>{renderTerm(s.expires_at)}</Td>
      <Td align="right" fit>
        <div className="relative" ref={ref}>
          <button
            onClick={() => setMenu((v) => !v)}
            disabled={busy}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-100 hover:bg-white/[0.06] transition-colors"
          >
            {busy ? "…" : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" />
              </svg>
            )}
          </button>
          {menu && (
            <div className="absolute right-0 top-8 z-20 min-w-[180px] rounded-xl card-elev shadow-pop py-1 text-sm">
              {s.link && (
                <MenuItem onClick={() => { setMenu(false); navigator.clipboard?.writeText(s.link!); popup({ tone: "success", title: "Ссылка скопирована" }); }}>
                  Скопировать ссылку
                </MenuItem>
              )}
              <MenuItem onClick={() => { setMenu(false); extend.mutate(); }}>Продлить +30 дней</MenuItem>
              <MenuItem onClick={async () => { setMenu(false); if (await confirmPopup({ title: "Сменить секрет?", body: "Старая ссылка юзера перестанет работать, придёт новая.", confirmLabel: "Сменить" })) rotate.mutate(); }}>
                Ротация секрета
              </MenuItem>
              {s.is_active && (
                <MenuItem onClick={async () => { setMenu(false); if (await confirmPopup({ title: "Деактивировать?", body: "Секрет снимется с выхода.", danger: true, confirmLabel: "Деактивировать" })) deactivate.mutate(); }}>
                  Деактивировать
                </MenuItem>
              )}
              <div className="my-1 border-t border-white/[0.06]" />
              <MenuItem danger onClick={async () => { setMenu(false); if (await confirmPopup({ title: "Удалить подписку?", body: `#${s.id} — необратимо.`, danger: true, confirmLabel: "Удалить" })) del.mutate(); }}>
                Удалить
              </MenuItem>
            </div>
          )}
        </div>
      </Td>
    </tr>
  );
}

function VpnTab() {
  const [vsearch, setVsearch] = useState("");
  const [vplan, setVplan] = useState("");
  const [vstatus, setVstatus] = useState("active");
  const q = useQuery<{ available: boolean; reason?: string; subscriptions: VpnSub[] }>({
    queryKey: ["vpn-subscriptions"],
    queryFn: () => api("/api/admin/vpn/subscriptions"),
    refetchInterval: 30000,
  });
  const d = q.data;

  if (q.isLoading) return <div className="text-zinc-400">Загрузка…</div>;
  if (!d?.available)
    return (
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-6 text-sm text-amber-300/90 max-w-2xl">
        VPN-подписки живут в Remnawave (истина по VPN — там). {d?.reason ? `Сейчас недоступно: ${d.reason}.` : ""}
        <div className="text-zinc-500 mt-2">На проде здесь появятся все VPN-юзера с их статусом, сроком и устройствами.</div>
      </div>
    );
  const all = d.subscriptions ?? [];
  const subs = all.filter((v) => {
    if (vplan && v.plan !== vplan) return false;
    if (vstatus === "active" && v.status !== "ACTIVE") return false;
    if (vstatus === "inactive" && v.status === "ACTIVE") return false;
    if (vsearch.trim()) {
      const t = vsearch.trim().toLowerCase().replace(/^@/, "");
      const hay = `${v.local_username ?? ""} ${v.username ?? ""} ${v.telegram_id ?? ""} ${v.user_id ?? ""}`;
      if (!hay.toLowerCase().includes(t)) return false;
    }
    return true;
  });

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input
          value={vsearch}
          onChange={(e) => setVsearch(e.target.value)}
          placeholder="Поиск: user_id, telegram_id или @username"
          className="max-w-xs"
        />
        <div className="flex items-center gap-1">
          {[["", "Все"], ["paid", "Платные"], ["trial", "Пробные"]].map(([k, l]) => (
            <FilterChip key={k || "all"} active={vplan === k} onClick={() => setVplan(k)}>
              {l}
            </FilterChip>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {[["active", "Активные"], ["", "Все"], ["inactive", "Выкл"]].map(([k, l]) => (
            <FilterChip key={k || "all"} active={vstatus === k} onClick={() => setVstatus(k)}>
              {l}
            </FilterChip>
          ))}
        </div>
        <div className="ml-auto text-zinc-500 text-sm">
          найдено: {subs.length.toLocaleString("ru-RU")}
          {subs.length !== all.length && (
            <span className="text-zinc-600"> · всего {all.length.toLocaleString("ru-RU")}</span>
          )}
        </div>
      </div>

      {subs.length === 0 ? (
        <div className="text-zinc-500">Ничего не найдено.</div>
      ) : (
        <Table>
          {/* Фиксированные ширины: без них колонки прыгали при смене фильтра —
              «пробный»/«платный» разной длины, и таблица переразмечалась. */}
          <colgroup>
            <col />
            <col style={{ width: "150px" }} />
            <col style={{ width: "130px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "150px" }} />
          </colgroup>
          <thead>
            <tr>
              <Th>Пользователь</Th>
              <Th>Тип</Th>
              <Th>Статус</Th>
              <Th>Трафик</Th>
              <Th>Срок</Th>
            </tr>
          </thead>
          <tbody>
            {subs.map((v, i) => (
              <tr
                key={i}
                className={`group hover:bg-white/[0.03] transition-colors ${
                  v.status === "ACTIVE" ? "" : "opacity-55"
                }`}
              >
                <Td>
                  <div className="min-w-0">
                    {v.user_id ? (
                      <Link
                        to={`/users/${v.user_id}`}
                        className="block truncate text-zinc-100 hover:text-brand font-medium"
                      >
                        {v.local_username ? `@${v.local_username}` : `#${v.user_id}`}
                      </Link>
                    ) : (
                      <span className="block truncate text-zinc-400">
                        {v.username || (v.telegram_id ? `tg${v.telegram_id}` : "—")}
                      </span>
                    )}
                    <div className="text-[11px] text-zinc-500 font-mono">
                      {v.telegram_id ?? "—"}
                      {v.user_id ? ` · user #${v.user_id}` : " · нет в нашей БД"}
                    </div>
                  </div>
                </Td>
                <Td>
                  <Pill tone={v.plan === "paid" ? "success" : "neutral"}>
                    {v.plan === "paid" ? "платный" : v.plan === "trial" ? "пробный" : "не размечен"}
                  </Pill>
                </Td>
                <Td>
                  <Pill tone={v.status === "ACTIVE" ? "success" : "neutral"}>
                    {v.status === "ACTIVE" ? "активна" : "выключена"}
                  </Pill>
                </Td>
                <Td>
                  <div className="text-sm text-zinc-300">{fmtBytes(v.used_traffic)}</div>
                  <div className="text-[11px] text-zinc-600">{v.devices ?? "—"} устр.</div>
                </Td>
                <Td>{renderTerm(v.expires_at)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`h-8 px-3 rounded-lg text-xs border transition-all ${
        active ? "bg-brand-soft text-brand border-brand/40" : "bg-black/20 border-white/[0.08] text-zinc-400 hover:text-zinc-200"
      }`}
    >
      {children}
    </button>
  );
}

function MenuItem({ children, onClick, danger = false }: { children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3.5 py-2 transition-colors ${danger ? "text-rose-400 hover:bg-rose-500/10" : "text-zinc-300 hover:bg-white/[0.05]"}`}
    >
      {children}
    </button>
  );
}
